import { Group, Stack } from '@mantine/core'
import { IconFolders } from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from 'redux'
import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import SectionTitle from 'app/views/components/Headers&Text/SectionTitle'
import { NewSelectInput } from 'app/views/components/inputs/selects/NewSelectInput'
import {
  ErrorNotification,
} from 'app/views/components/notifications/notification'
import { getFacility } from 'app/state/ducks/facilities/selectors'
import {
  FacilityHistoryFilterValues,
  GridColumnVisibilityStorage,
  GridPaginationStorage,
  loadFacilityHistoryFiltersFromStorage,
  loadFacilityHistorySortFromStorage,
  saveFacilityHistorySortToStorage,
  useGridColumnVisibility,
  useGridPagination,
} from 'app/utils/grid-sorting-storage'
import FacilityHistoryFilter from './facility-history-filter'
import FacilityHistoryReverseModal from './facility-history-reverse-modal'
import FacilityHistoryTable from './facility-history-table'
import { getTransactionLabel } from './facility-history-columns'
import { FacilityHistoryEntry } from './facility-history-types'
import { FacilityReverseParams } from 'app/models/facility-reverse-params'
import {
  approveFacilityReverse,
  createAndSubmitFacilityReverse,
  rejectAndRemoveFacilityReverse,
} from '../../../facility-reverses/facility-reverse-daml-actions'

const SORT_BY_OPTIONS = [
  { value: 'processedAt', label: 'Processed Date' },
  { value: 'effectiveDate', label: 'Effective Date' },
  { value: 'transactionType', label: 'Transaction Type' },
  { value: 'level', label: 'Level' },
]

const ORDER_OPTIONS = ['Ascending', 'Descending']

function applyFilters(
  entries: FacilityHistoryEntry[],
  filters: FacilityHistoryFilterValues,
): FacilityHistoryEntry[] {
  return entries.filter((entry) => {
    if (filters.level && entry.level !== filters.level) return false
    if (filters.transactionType && entry.transactionType !== filters.transactionType) {
      return false
    }
    if (filters.source && entry.source !== filters.source) return false
    return true
  })
}

function sortEntries(
  entries: FacilityHistoryEntry[],
  sortBy: string,
  order: 'Ascending' | 'Descending',
): FacilityHistoryEntry[] {
  const dir = order === 'Ascending' ? 1 : -1
  const sorted = [...entries].sort((a, b) => {
    const aValue =
      sortBy === 'transactionType'
        ? getTransactionLabel(a)
        : (a[sortBy as keyof FacilityHistoryEntry] as string | null) ?? ''
    const bValue =
      sortBy === 'transactionType'
        ? getTransactionLabel(b)
        : (b[sortBy as keyof FacilityHistoryEntry] as string | null) ?? ''
    return (aValue ?? '').toString().localeCompare((bValue ?? '').toString()) * dir
  })
  return sorted
}

export default function FacilityHistoryPage() {
  const { facilityId } = useParams<{ facilityId: string }>()
  const dispatch = useDispatch()
  const [pendingRevertId, setPendingRevertId] = useState<string | null>(null)
  // Mirrors the state so back-to-back clicks in the same tick are rejected —
  // React batches setState, so reading state alone would let the second through.
  const pendingRevertIdRef = useRef<string | null>(null)
  // Monotonic token identifying the newest feed request; stale ones bail out.
  const feedRequestIdRef = useRef(0)
  const facility = useSelector(getFacility)
  const [entries, setEntries] = useState<FacilityHistoryEntry[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [filterValues, setFilterValues] = useState<FacilityHistoryFilterValues>(
    () => loadFacilityHistoryFiltersFromStorage(),
  )
  const [{ sortBy, order }, setSortPref] = useState(() =>
    loadFacilityHistorySortFromStorage(),
  )
  const [reverseTarget, setReverseTarget] =
    useState<FacilityHistoryEntry | null>(null)

  const { pagination, handlePaginationChange } = useGridPagination(
    GridPaginationStorage.facilityHistory,
  )
  const { columnVisibility, handleColumnVisibilityChange } =
    useGridColumnVisibility(GridColumnVisibilityStorage.facilityHistory)

  const loadFeed = useCallback(async () => {
    if (!facilityId) return
    // The initial effect and the post-decision refresh can be in flight at the
    // same time. Without a token the slower response wins whenever it lands
    // second, and its `finally` clears the spinner while the newer request is
    // still running.
    const requestId = ++feedRequestIdRef.current
    setIsLoading(true)
    try {
      const response = await client.get<FacilityHistoryEntry[]>(
        `${config.backendUrl}/api/approved-facilities/history-feed?id=${facilityId}`,
      )
      if (requestId !== feedRequestIdRef.current) return
      setEntries(response.data ?? [])
    } catch {
      if (requestId !== feedRequestIdRef.current) return
      setEntries([])
    } finally {
      if (requestId === feedRequestIdRef.current) {
        setIsLoading(false)
      }
    }
  }, [facilityId])

  useEffect(() => {
    void loadFeed()
  }, [loadFeed])

  const visibleEntries = useMemo(
    () => sortEntries(applyFilters(entries, filterValues), sortBy, order),
    [entries, filterValues, sortBy, order],
  )

  const updateSort = (next: { sortBy?: string; order?: string }) => {
    const updated = {
      sortBy: next.sortBy ?? sortBy,
      order: (next.order ?? order) as 'Ascending' | 'Descending',
    }
    setSortPref(updated)
    saveFacilityHistorySortToStorage(updated)
  }

  const handleRevert = useCallback((entry: FacilityHistoryEntry) => {
    setReverseTarget(entry)
  }, [])

  // Approve/reject a submitted reversal without leaving the History tab. Both
  // go through the same helpers the servicing view uses, so the notifications
  // and ledger calls stay identical; the feed is then reloaded so the row
  // reflects the outcome (approved -> Reverted, rejected -> back to Draft).
  // Guards against a second decision landing on a reversal whose first one is
  // still in flight — approve twice, or approve then reject, before the feed
  // comes back. The id is cleared in `finally` so a failed request doesn't
  // strand the controls disabled.
  const decideRevert = useCallback(
    async (
      entry: FacilityHistoryEntry,
      decide: (
        reverse: FacilityReverseParams,
        dispatch: Dispatch<any>,
      ) => Promise<unknown>,
    ) => {
      const openRevert = entry.openRevert
      if (!openRevert) return
      if (pendingRevertIdRef.current === openRevert.id) return
      pendingRevertIdRef.current = openRevert.id
      setPendingRevertId(openRevert.id)
      try {
        await decide({ id: openRevert.id } as FacilityReverseParams, dispatch)
        await loadFeed()
      } finally {
        pendingRevertIdRef.current = null
        setPendingRevertId(null)
      }
    },
    [dispatch, loadFeed],
  )

  const handleApproveRevert = useCallback(
    (entry: FacilityHistoryEntry) => decideRevert(entry, approveFacilityReverse),
    [decideRevert],
  )

  // Rejecting returns the reversal to Draft on the ledger, which would linger
  // as an open reverse and keep the transaction un-revertible. The helper
  // deletes that leftover draft, but only once the rejection itself succeeded.
  const handleRejectRevert = useCallback(
    (entry: FacilityHistoryEntry) =>
      decideRevert(entry, rejectAndRemoveFacilityReverse),
    [decideRevert],
  )

  const handleReverseSubmit = useCallback(
    async (entry: FacilityHistoryEntry, reason: string) => {
      if (!entry.transactionId) {
        ErrorNotification({
          title: 'Cannot reverse',
          message: 'This entry has no transaction id and cannot be reversed.',
        })
        return
      }
      if (!facility?.id || !facility?.accountManagementAdmin) {
        ErrorNotification({
          title: 'Cannot reverse',
          message: 'Facility identity is not available — please reload.',
        })
        return
      }

      try {
        // Create + submit in one go through the duck, so the reversal lands in
        // Submitted where a manager can act on it — and so the store updates in
        // flight rather than waiting on a refetch.
        const submitted = await createAndSubmitFacilityReverse(
          {
            facilityId: {
              id: facility.id,
              admin: facility.accountManagementAdmin,
            },
            transactionId: entry.transactionId,
            reason,
          } as FacilityReverseParams,
          dispatch,
        )
        if (!submitted) return
        setReverseTarget(null)
        // Refresh so the just-reversed transaction's button disables (it now has an open reverse).
        void loadFeed()
      } catch (error) {
        console.error('[facility-history] reverse failed', error)
        ErrorNotification({
          title: 'Reversal failed',
          message:
            (error as { response?: { data?: { message?: string } } })?.response
              ?.data?.message ?? 'Could not create the reversal draft.',
        })
      }
    },
    [facility?.id, facility?.accountManagementAdmin, loadFeed],
  )

  return (
    <Stack w="100%">
      <SectionTitle>Filter</SectionTitle>
      <Stack
        w="100%"
        className="sectionRemoveGap"
        style={{
          backgroundColor: '#f7f7f7',
          borderBottom: '1px solid #B4B3B3',
          borderLeft: '1px solid #B4B3B3',
          borderRight: '1px solid #B4B3B3',
        }}
      >
        <FacilityHistoryFilter
          filterValues={filterValues}
          setFilterValues={setFilterValues}
        />
      </Stack>
      <Stack
        w="100%"
        className="sectionRemoveGap sectionBody"
        style={{
          padding: '10px 20px 20px 20px',
          marginTop: '10px',
          backgroundColor: '#F5EEEE',
          border: '1px solid #B4B3B3',
        }}
      >
        <Group className="tradeTableHeader">
          <div className="tradeTotalAndReportGroup">
            <div className="tradeTotalGroup">
              <IconFolders />
              <span style={{ color: '#784595' }}>Total History: </span>
              <span>{visibleEntries.length}</span>
            </div>
          </div>
          <Group
            className="tradeSortGroup"
            style={{ justifyContent: 'flex-end', marginRight: '22px' }}
          >
            <NewSelectInput
              wrapperType="trade"
              label="Sort By"
              data={SORT_BY_OPTIONS}
              w="200px"
              h="42px"
              size="18px"
              placeholder="Sort By"
              value={sortBy}
              onChange={(value) => updateSort({ sortBy: value ?? 'processedAt' })}
            />
            <NewSelectInput
              wrapperType="trade"
              label="Order"
              data={ORDER_OPTIONS}
              w="164px"
              h="42px"
              placeholder="Order"
              value={order}
              onChange={(value) => updateSort({ order: value ?? 'Ascending' })}
            />
          </Group>
        </Group>
        <Stack w="100%" pt="12px" mt="10px" style={{ padding: 0 }}>
          <FacilityHistoryTable
            entries={visibleEntries}
            isLoading={isLoading}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            onRevert={handleRevert}
            onApproveRevert={handleApproveRevert}
            onRejectRevert={handleRejectRevert}
            pendingRevertId={pendingRevertId}
          />
        </Stack>
      </Stack>
      <FacilityHistoryReverseModal
        entry={reverseTarget}
        opened={reverseTarget !== null}
        onClose={() => setReverseTarget(null)}
        onSubmit={handleReverseSubmit}
      />
    </Stack>
  )
}
